/**
 * Page-limited access, the app's half (docs/ACCESS.md § Page-limited access). The API
 * decides and enforces it (api/shared/pages.ts): a limited reader's bundle carries
 * `pages`, the page keys they may open, and every other endpoint answers them 403.
 * This only shows them those pages, and says so plainly on any other route.
 *
 * Read off the bundle, not asked for separately: it is the bundle the API scoped for
 * this reader — or for the person being viewed as — so the two cannot disagree.
 * DatasetProvider hands it down as PageLimitCtx; outside one (a test rendering a single
 * page) there is no limit.
 */
import { createContext, useContext } from 'react'
import { Link } from 'react-router-dom'

/** The bundle's `pages`, or null: provided by DatasetProvider. */
export const PageLimitCtx = createContext<readonly string[] | null>(null)

/** Page key (api/shared/pages.ts LIMITED_PAGES) → route and name. */
export const LIMITED_PAGES: Record<string, { path: string; label: string }> = {
  terminated: { path: '/terminated', label: 'Terminated worklists' },
  'claim-detail': { path: '/claim-detail', label: 'Claim detail' },
}

/** The routes `pages` opens, in LIMITED_PAGES order; `null` when there is no limit. Unknown keys open nothing. */
export function allowedPaths(pages: readonly string[] | null | undefined): string[] | null {
  if (!pages) return null
  return Object.entries(LIMITED_PAGES)
    .filter(([k]) => pages.includes(k))
    .map(([, p]) => p.path)
}

/** Whether `pathname` is open to a reader limited to `allowed`. */
export const pathAllowed = (allowed: readonly string[] | null, pathname: string) =>
  allowed === null || allowed.includes(pathname)

/** This reader's open routes, or `null` for no limit. */
export function usePageLimit(): string[] | null {
  return allowedPaths(useContext(PageLimitCtx))
}

/** Whether this reader may open `path`: for links into other pages. */
export function usePageAllowed(path: string): boolean {
  return pathAllowed(usePageLimit(), path)
}

/** What a limited reader sees on any other route: which pages they have, as links. */
export function PageLimitedNotice({ allowed }: { allowed: readonly string[] }) {
  const pages = Object.values(LIMITED_PAGES).filter((p) => allowed.includes(p.path))
  const names = pages.map((p) => p.label)
  const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : (names[0] ?? 'no pages')
  return (
    <div className="notice" role="note" data-testid="page-limited" style={{ marginTop: 16, maxWidth: 640 }}>
      <span className="eyebrow">Not part of your access</span>
      <p className="notice-what">You have access to {list}.</p>
      {pages.length > 0 ? (
        <p className="notice-why">
          {pages.map((p, i) => (
            <span key={p.path}>
              {i > 0 && ' · '}
              <Link to={p.path} className="cell-link">
                {p.label}
              </Link>
            </span>
          ))}
        </p>
      ) : (
        <p className="notice-why">Ask the Cost per Claim app owner if you need more.</p>
      )}
    </div>
  )
}
